/**
 * Analytics Module
 *
 * Main exports for Google Analytics 4 tracking
 */

import ReactGA from 'react-ga4';
import { useAnalyticsConsent } from '../contexts/AnalyticsContext';
import { IS_ANALYTICS_ENABLED } from './config';
import type { CustomEvent, StandardEvent } from './events';

// Re-export types and constants
export * from './events';
export { GoogleAnalytics } from './GoogleAnalytics';
export { ButtonTracker } from './ButtonTracker';

/**
 * Hook for tracking custom events
 *
 * @returns {Object} Analytics tracking functions
 * @example
 * ```tsx
 * const { trackEvent, isEnabled } = useAnalytics();
 *
 * trackEvent({
 *   category: EventCategory.CASE,
 *   action: 'case_created',
 *   label: 'tipo_causa_value',
 *   value: 1,
 * });
 * ```
 */
export const useAnalytics = () => {
  const { hasConsent } = useAnalyticsConsent();

  /**
   * Track a custom event
   */
  const trackEvent = (event: CustomEvent) => {
    if (!IS_ANALYTICS_ENABLED || !hasConsent) {
      return;
    }

    try {
      ReactGA.event(event.action, {
        ...event,
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  };

  /**
   * Track a standard GA4 event
   */
  const trackStandardEvent = (eventName: StandardEvent, params?: Record<string, any>) => {
    if (!IS_ANALYTICS_ENABLED || !hasConsent) {
      return;
    }

    try {
      ReactGA.event(eventName, params);
    } catch (error) {
      console.error('Failed to track standard event:', error);
    }
  };

  return {
    trackEvent,
    trackStandardEvent,
    isEnabled: IS_ANALYTICS_ENABLED && hasConsent,
  };
};

/**
 * Utility function for tracking events outside React components
 *
 * Use this in service files, utilities, or anywhere outside the React tree.
 *
 * @example
 * ```ts
 * import { trackEvent, AnalyticsEvents } from './analytics';
 *
 * trackEvent(AnalyticsEvents.LOGIN_SUCCESS);
 * ```
 */
export const trackEvent = (event: CustomEvent) => {
  const consent = localStorage.getItem('analytics_consent') === 'true';

  if (!IS_ANALYTICS_ENABLED || !consent) {
    return;
  }

  try {
    ReactGA.event(event.action, {
      ...event,
    });
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};
